// TagsAdmin — 태그 관리 페이지 (/tags).
// 태그 목록 표시, 이름 변경, 색상 변경, 삭제 기능 제공.
// 신규 태그는 항목 모달(TagChipInput)에서 자동 생성하므로 여기서는 관리(편집/삭제)에 집중.

import { useState } from 'react';
import { useTags, useTagMutations } from '../hooks/useTags';
import { useToast } from '../components/Toast';
import { formatApiError } from '../api/client';
import { Spinner } from '../components/Spinner';

// ─── 색상 팔레트 (빠른 선택용) ──────────────────────────

const COLOR_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  '#6366f1', '#06b6d4', '#84cc16', '#f43f5e',
];

// ─── TagsAdmin 메인 ──────────────────────────────────────

export default function TagsAdmin() {
  const { data: tags = [], isLoading } = useTags('', 500);
  const { createTag, updateTag, deleteTag } = useTagMutations();
  const toast = useToast();

  // 인라인 편집 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>('');

  // 신규 태그 생성 폼 상태
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // ─── 편집 시작 ──────────────────────────────────────

  const handleEditStart = (id: number, name: string, color: string | null) => {
    setEditingId(id);
    setEditName(name);
    setEditColor(color ?? '');
  };

  // ─── 편집 저장 ──────────────────────────────────────

  const handleEditSave = async (id: number) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error('태그 이름을 입력하세요');
      return;
    }
    try {
      await updateTag.mutateAsync({
        id,
        patch: {
          name: trimmed,
          color: editColor || null,
        },
      });
      toast.success('태그가 수정되었습니다');
      setEditingId(null);
    } catch (err) {
      toast.error(formatApiError(err, '태그 수정 실패'));
    }
  };

  // ─── 삭제 ──────────────────────────────────────────

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`태그 "${name}"을 삭제하시겠습니까?\n이 태그가 붙은 모든 항목에서 제거됩니다.`)) return;
    try {
      await deleteTag.mutateAsync(id);
      toast.success('태그가 삭제되었습니다');
    } catch (err) {
      toast.error(formatApiError(err, '태그 삭제 실패'));
    }
  };

  // ─── 신규 생성 ──────────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      await createTag.mutateAsync({
        name: trimmed,
        color: newColor || null,
      });
      toast.success('태그가 생성되었습니다');
      setNewName('');
      setNewColor('');
      setShowCreateForm(false);
    } catch (err) {
      toast.error(formatApiError(err, '태그 생성 실패'));
    }
  };

  // ─── 렌더 ──────────────────────────────────────────

  return (
    <div className="tags-admin stack">
      <div className="tags-admin__header">
        <h2>태그 관리</h2>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setShowCreateForm((v) => !v)}
        >
          + 태그 추가
        </button>
      </div>

      {/* 신규 태그 생성 폼 */}
      {showCreateForm && (
        <form className="tags-admin__create-form card" onSubmit={handleCreate}>
          <h3>새 태그 만들기</h3>
          <div className="tags-admin__form-row">
            <label htmlFor="new-tag-name">이름</label>
            <input
              id="new-tag-name"
              type="text"
              className="input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="태그 이름"
              required
            />
          </div>
          <div className="tags-admin__form-row">
            <label>색상</label>
            <ColorPicker value={newColor} onChange={setNewColor} />
          </div>
          <div className="tags-admin__form-actions">
            <button type="submit" className="btn btn--primary" disabled={createTag.isPending}>
              {createTag.isPending ? '생성 중...' : '생성'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setShowCreateForm(false)}>
              취소
            </button>
          </div>
        </form>
      )}

      {/* 태그 목록 */}
      {isLoading ? (
        <Spinner />
      ) : tags.length === 0 ? (
        <p className="muted">태그가 없습니다. 항목 상세에서 태그를 추가하면 자동 생성됩니다.</p>
      ) : (
        <table className="tags-admin__table">
          <thead>
            <tr>
              <th>색상</th>
              <th>이름</th>
              <th>항목 수</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => (
              <tr key={tag.id} className="tags-admin__row">
                {editingId === tag.id ? (
                  // ─── 편집 행 ────────────────────────
                  <>
                    <td>
                      <ColorPicker value={editColor} onChange={setEditColor} compact />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="input input--sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditSave(tag.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        autoFocus
                      />
                    </td>
                    <td className="muted">{tag.item_count}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--primary"
                        onClick={() => handleEditSave(tag.id)}
                        disabled={updateTag.isPending}
                      >
                        저장
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        onClick={() => setEditingId(null)}
                      >
                        취소
                      </button>
                    </td>
                  </>
                ) : (
                  // ─── 표시 행 ────────────────────────
                  <>
                    <td>
                      <span
                        className="tags-admin__color-dot"
                        style={{ backgroundColor: tag.color ?? '#888' }}
                        aria-hidden="true"
                      />
                    </td>
                    <td className="tags-admin__name">{tag.name}</td>
                    <td className="muted">{tag.item_count}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        onClick={() => handleEditStart(tag.id, tag.name, tag.color)}
                      >
                        편집
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={() => handleDelete(tag.id, tag.name)}
                        disabled={deleteTag.isPending}
                      >
                        삭제
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── ColorPicker 서브 컴포넌트 ───────────────────────────

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  /** compact 모드: 팔레트만 표시 (텍스트 입력 없음) */
  compact?: boolean;
}

function ColorPicker({ value, onChange, compact = false }: ColorPickerProps) {
  return (
    <div className="color-picker">
      <div className="color-picker__palette">
        {COLOR_PALETTE.map((c) => (
          <button
            key={c}
            type="button"
            className={`color-picker__swatch${value === c ? ' color-picker__swatch--selected' : ''}`}
            style={{ backgroundColor: c }}
            onClick={() => onChange(value === c ? '' : c)}
            aria-label={`색상 선택: ${c}`}
            aria-pressed={value === c}
            title={c}
          />
        ))}
      </div>
      {!compact && (
        <input
          type="text"
          className="input input--sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#hex 또는 비워두면 자동"
          maxLength={7}
        />
      )}
      {value && (
        <span
          className="color-picker__preview"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
